var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { FieldWrapper, IconButton } from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { isHotkey } from 'is-hotkey';
import trim from 'lodash/trim';
import { useRef, useState, } from 'react';
import { useEffectOnce } from 'react-use';
import { VariableSelector } from './components/variableSelector';
import styles from './tokenInput.module.scss';
import { getTokenString } from './utils/getTokenString/getTokenString';
import { renderNodeToString } from './utils/renderNodeToString/renderNodeToString';
import { replaceTokensWithTags } from './utils/replaceTokensWithTags/replaceTokensWithTags';
export const TokenInput = (props) => {
    const { className, variables, placeholder, label, input, meta, inputClassName, inputStyle, mode, withVariableSearch } = props, otherProps = __rest(props, ["className", "variables", "placeholder", "label", "input", "meta", "inputClassName", "inputStyle", "mode", "withVariableSearch"]);
    const [hasFocus, setHasFocus] = useState(false);
    const [lastSelection, setLastSelection] = useState(null);
    const rootClass = classnames(styles.root, className, {
        [styles.rootFocus]: hasFocus,
        [styles.rootInput]: mode === 'input',
        [styles.rootTextarea]: mode === 'textarea',
    });
    const inputRef = useRef(null);
    const handleKeyDown = (event) => {
        if (isHotkey('mod+b', event) || isHotkey('mod+i', event)) {
            event.preventDefault();
            return;
        }
        if (event.key === 'Enter') {
            event.preventDefault();
            if (mode === 'textarea') {
                document.execCommand('insertLineBreak');
            }
        }
    };
    const handlePaste = (event) => {
        event.preventDefault();
        const { clipboardData } = event;
        if (!clipboardData) {
            return;
        }
        const text = clipboardData.getData('text/plain');
        document.execCommand('insertHTML', false, text);
    };
    const handleInput = () => {
        const { current } = inputRef;
        if (current) {
            // Chrome execCommand on Chrome insert an extra span with className styles resolved to inline styles.
            // We don't want that element to appear, so we remove it
            current.querySelectorAll('[style]').forEach((el) => {
                if (el.parentNode) {
                    el.parentNode.removeChild(el);
                }
            });
            const text = trim(renderNodeToString(current), '\n');
            input.onChange(text);
        }
    };
    const handleFocus = () => {
        setHasFocus(true);
    };
    const handleBlur = () => {
        const selection = window.getSelection();
        setHasFocus(false);
        setLastSelection(selection ? selection.getRangeAt(0) : null);
    };
    const handleAddToken = (name, label) => {
        if (!hasFocus) {
            const { current } = inputRef;
            if (current) {
                if (lastSelection) {
                    const selection = window.getSelection();
                    if (selection === null) {
                        return;
                    }
                    selection.removeAllRanges();
                    selection.addRange(lastSelection);
                }
                else {
                    current.focus();
                }
            }
        }
        document.execCommand('insertHTML', false, getTokenString(name, label));
    };
    useEffectOnce(() => {
        const { current } = inputRef;
        if (current) {
            const temp = document.createElement('div');
            temp.textContent = input.value;
            // This is perfectly sanitary.
            // eslint-disable-next-line no-unsanitized/property
            current.innerHTML = replaceTokensWithTags(temp.innerHTML, variables);
        }
    });
    return (_jsx(FieldWrapper, Object.assign({}, otherProps, { className: rootClass, label: label, meta: meta }, { children: _jsxs("div", Object.assign({ className: styles.wrapper }, { children: [_jsxs("div", Object.assign({ className: classnames(styles.input, styles.inputAligned) }, { children: [_jsx("div", { style: inputStyle, id: "input", className: classnames(inputClassName, styles.inputInner, styles.inputInnerAligned), contentEditable: true, onKeyDown: handleKeyDown, onInput: handleInput, onPaste: handlePaste, onFocus: handleFocus, onBlur: handleBlur, ref: inputRef, role: "textbox", tabIndex: 0 }), input.value.length === 0 && !!placeholder && (_jsx("span", Object.assign({ className: styles.placeholder }, { children: placeholder })))] })), _jsx(VariableSelector, Object.assign({ onSelect: handleAddToken, variables: variables, className: styles.dropdownSelect, withSearch: withVariableSearch }, { children: _jsx(IconButton, { icon: "dropdown-list", variant: "tertiary", className: styles.addButton }) }))] })) })));
};
